import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { parseFromDecimal } from 'utils/parser.utils';

import { ClaimableSummary, EarningsTableStatus, StakingKPIs, UserDeposit, UserStakingTotals } from '../types';

interface UseStakingKpisProps {
  balance: string;
  totals: UserStakingTotals;
  deposits: UserDeposit[];
  tokenDecimals: number;
  minDeposit: string;
}

interface UseStakingKpisReturn {
  kpis: StakingKPIs;
  claimableSummary: ClaimableSummary;
  minDepositDisplay: number;
}

/**
 * Hook for computing staking KPIs and claimable summary.
 * Derives display values from raw blockchain data.
 */
export const useStakingKpis = ({
  balance,
  totals,
  deposits,
  tokenDecimals,
  minDeposit,
}: UseStakingKpisProps): UseStakingKpisReturn => {
  const minDepositDisplay = useMemo(() => {
    if (!minDeposit || minDeposit === '0') return 1;
    const parsed = parseFloat(parseFromDecimal(minDeposit, tokenDecimals));
    // If parsed value is too small or invalid, use 1 as default minimum
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }, [minDeposit, tokenDecimals]);

  const kpis: StakingKPIs = useMemo(() => {
    const balanceNum = parseFloat(balance) || 0;

    const combinedTotalStaked = new Decimal(totals.periodicalTotalStaked)
      .add(totals.whitelistedTotalStaked)
      .add(totals.vipTotalStaked)
      .toString();

    const activeDeposits = deposits.filter(
      (d) => d.status !== EarningsTableStatus.WITHDRAWN && d.status !== EarningsTableStatus.CLAIMED
    );
    const combinedRewardsExpected = activeDeposits
      .reduce((sum, d) => new Decimal(sum).add(d.rewardsRaw).toString(), '0');

    const stakedNum = parseFloat(parseFromDecimal(combinedTotalStaked, tokenDecimals)) || 0;
    const rewardsNum = parseFloat(parseFromDecimal(combinedRewardsExpected, tokenDecimals)) || 0;

    return {
      balance: combinedTotalStaked,
      balanceDisplay: balanceNum,
      totalStaked: combinedTotalStaked,
      totalStakedDisplay: stakedNum,
      totalRewardsExpected: combinedRewardsExpected,
      totalRewardsExpectedDisplay: rewardsNum,
    };
  }, [
    balance,
    deposits,
    totals.periodicalTotalStaked,
    totals.whitelistedTotalStaked,
    totals.vipTotalStaked,
    tokenDecimals,
  ]);

  const claimableSummary: ClaimableSummary = useMemo(() => {
    const isFlexible = (d: UserDeposit) => d.periodDays === '0';
    const isFixedPeriodActive = (d: UserDeposit) => !isFlexible(d) && d.daysLeft !== null && d.daysLeft > 0;

    const depositsForStaked = deposits.filter((d) => (d.canWithdraw || d.canClaim) && !isFlexible(d) && !isFixedPeriodActive(d));
    const depositsForRewards = deposits.filter((d) => d.canClaim && !isFixedPeriodActive(d));

    const combinedStaked = depositsForStaked
      .reduce((sum, d) => new Decimal(sum).add(d.amountRaw).toString(), '0');

    const combinedRewards = depositsForRewards
      .reduce((sum, d) => new Decimal(sum).add(d.rewardsRaw).toString(), '0');

    const totalClaimable = new Decimal(combinedStaked).add(combinedRewards).toString();

    const totalAmountDisplay = parseFloat(parseFromDecimal(totalClaimable, tokenDecimals)) || 0;
    const stakedAmountDisplay = parseFloat(parseFromDecimal(combinedStaked, tokenDecimals)) || 0;
    const rewardsAmountDisplay = parseFloat(parseFromDecimal(combinedRewards, tokenDecimals)) || 0;

    const claimableDeposits = deposits.filter((d) => (d.canClaim || d.canWithdraw) && !isFixedPeriodActive(d));
    const periodicalClaimableCount = claimableDeposits.filter((d) => !d.isWhitelisted && !d.isVip).length;
    const whitelistedClaimableCount = claimableDeposits.filter((d) => d.isWhitelisted).length;
    const vipClaimableCount = claimableDeposits.filter((d) => d.isVip).length;

    return {
      totalAmount: totalClaimable,
      totalAmountDisplay,
      stakedAmount: combinedStaked,
      stakedAmountDisplay,
      rewardsAmount: combinedRewards,
      rewardsAmountDisplay,
      hasClaimable: totalAmountDisplay > 0,
      periodicalClaimableCount,
      whitelistedClaimableCount,
      vipClaimableCount,
    };
  }, [deposits, tokenDecimals]);

  return {
    kpis,
    claimableSummary,
    minDepositDisplay,
  };
};
